import { useCallback, useEffect, useState } from "react";
import { site } from "./current";
import { BTN_PRIMARY, CARD, Notice, PageTitle } from "./admin/ui";

// Publish: what you save in the dashboard is stored on the website at once; the public pages are ready-made files,
// rebuilt from the saved content by GitHub (the website's deploy workflow) when someone presses Publish. Takes 3–5
// minutes. While the coming-soon switch is on, publishing updates the hidden full site too, ready for launch.

type Run = { status: string; conclusion: string | null; startedAt: string; url: string; byButton: boolean };

const when = (iso: string) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const label = (r: Run) =>
  r.status !== "completed" ? (r.status === "queued" ? "Waiting to start…" : "Publishing…") : r.conclusion === "success" ? "Published" : r.conclusion === "cancelled" ? "Cancelled" : "Failed";

export function PublishPanel() {
  const [state, setState] = useState<{ configured: boolean; runs: Run[] } | null>(null);
  const [notice, setNotice] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const answer = await site().get<{ configured: boolean; runs: Run[] }>("/publish");
      setState({ configured: answer.configured, runs: answer.runs ?? [] });
    } catch (error) {
      setNotice({ type: "error", text: error instanceof Error ? error.message : "Couldn't read the publish status." });
    }
  }, []);

  const running = state?.runs.some((r) => r.status !== "completed") ?? false;
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- loads once, then every 15 s while a publish runs
    void load();
    if (!running) return;
    const timer = window.setInterval(load, 15_000);
    return () => window.clearInterval(timer);
  }, [load, running]);

  const publish = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const answer = await site().post<{ ok: boolean; error?: string }>("/publish");
      setNotice(answer.ok ? { type: "ok", text: "Publishing started. The website updates in 3–5 minutes." } : { type: "error", text: answer.error ?? "Couldn't start publishing." });
      window.setTimeout(load, 4000);
    } catch (error) {
      setNotice({ type: "error", text: error instanceof Error ? error.message : "Couldn't start publishing." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageTitle title="Publish" />
      <p className="-mt-3 mb-5 text-midnight/70">
        Everything you save is kept on the website at once. Publish puts it on the public pages: the website is rebuilt from the saved content, which
        takes 3–5 minutes. Publish once after a batch of changes.
      </p>
      <div className={`${CARD} flex flex-col gap-4`}>
        {state && !state.configured ? (
          <p>Publishing isn&apos;t set up yet: the website still needs its GitHub publish token. Ask the developer.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={publish} disabled={busy || running || !state} className={`${BTN_PRIMARY} disabled:opacity-60`}>
              {running ? "Publishing…" : busy ? "Starting…" : "Publish now"}
            </button>
            {running && <span className="text-sm text-midnight/70">A publish is already running; this page updates by itself.</span>}
          </div>
        )}
        <Notice notice={notice} />
      </div>
      <h2 className="mt-8 mb-3 text-xl font-bold">Latest</h2>
      {state?.runs.length ? (
        <ul className="flex flex-col gap-2">
          {state.runs.map((r) => (
            <li key={r.url} className="flex flex-wrap items-baseline justify-between gap-2 rounded-xl border border-midnight/10 bg-sand px-4 py-3">
              <span className="font-bold">{label(r)}</span>
              <span className="text-sm text-midnight/70">
                {when(r.startedAt)} · {r.byButton ? "Publish button" : "new website code"} ·{" "}
                <a href={r.url} target="_blank" rel="noreferrer" className="text-teal underline underline-offset-4">
                  details ↗
                </a>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-midnight/70">{state ? "Nothing published yet." : "Loading…"}</p>
      )}
    </div>
  );
}
